import { quantizeToStep, quantizeLogStep, logStepValue, valueToFraction, fractionToValue, computeTickPositions, type SliderScaleMode, type SliderOrientation, type SliderTickLabel } from '@agavi/blocks';
import React, { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { speakNumber } from './number-format';

export interface SliderTrackProps {
  min: number;
  max: number;
  scaleMode: SliderScaleMode;
  stepSize: number;
  logStepsPerDecade: number;
  /** Current position to render — unquantized while actively dragging (for smooth visual
   * tracking), quantized at rest. */
  value: number;
  /** Fired once at the very start of any interaction (first pointerdown or first keypress),
   * regardless of where the handle ends up — this is the touched-state signal, kept separate
   * from onChange/onCommit so "did the student interact at all" never depends on whether the
   * value actually moved. */
  onTouch: () => void;
  /** Continuous updates during a drag (raw, unquantized) — drives smooth visual tracking. Also
   * fired (already-quantized) on every keyboard step. The displayed live-readout number is
   * always the quantized value regardless — showing more precision than the step allows is
   * misleading, since those in-between values are never actually selectable. */
  onChange: (value: number) => void;
  /** The final, quantized value — fired on pointer release and on every keyboard step. */
  onCommit: (value: number) => void;
  /** Second handle (dual-handle mode) — omit entirely for a single-handle track. Handle A is
   * always treated as the lower-value handle and handle B the upper-value one; each clamps
   * against the other's current value so they can't cross. */
  valueB?: number;
  onTouchB?: () => void;
  onChangeB?: (value: number) => void;
  onCommitB?: (value: number) => void;
  ariaLabelB?: string;
  showLiveReadout: boolean;
  minLabel?: string;
  maxLabel?: string;
  orientation: SliderOrientation;
  reverseDirection: boolean;
  showTickMarks?: boolean;
  majorTickInterval?: number;
  minorTickInterval?: number;
  showMajorTickValues?: boolean;
  tickLabels?: SliderTickLabel[];
  /** Formats a bare number — no unit suffix. Unit suffix is a separate prop so a dual-handle
   * readout can show it once ("5 – 15 days") instead of once per side ("5 days – 15 days"). */
  formatValue: (value: number) => string;
  unitSuffix?: string;
  /** Names for the nudge pair. Required whenever a lattice exists — the buttons are the only
   *  control here with no visible text of its own. */
  ariaLabelNudgeDown?: string;
  ariaLabelNudgeUp?: string;
  /**
   * The second handle's nudge pair, when there are two.
   *
   * A whole second name rather than a handle word prefixed onto the first: this used to glue
   * `${handleLabel} ${direction}` together and announce "Slider 1 Decrease by one step", which is
   * two fragments with a capital in the middle. The caller interpolates one sentence instead.
   */
  ariaLabelNudgeDownB?: string;
  ariaLabelNudgeUpB?: string;
  /** Which slider a pair belongs to, shown beside it whenever there are two. VISIBLE text on an
   *  `aria-hidden` span, not a name — the buttons carry their own. */
  handleLabel1?: string;
  handleLabel2?: string;
  /** Keep the VERTICAL track at its short fixed height — for a mockup in a small panel,
   *  never for a track a student can drag. See TRACK_LENGTH_COMPACT_PX. */
  compact?: boolean;
  disabled?: boolean;
  ariaLabel: string;
}

/** Fraction-distance below which the two handles are considered visually overlapping — below
 * this, a plain click no longer reliably distinguishes "which handle did the user mean," so
 * pointerdown defers to direction-of-first-movement instead (see handlePointerDown/Move). */
const OVERLAP_FRACTION_THRESHOLD = 0.035;
/** Minimum pixel movement before an ambiguous (overlapping) pointerdown commits to a handle —
 * avoids committing on click-jitter before the user has actually indicated a direction. */
const AMBIGUOUS_COMMIT_PX = 3;
/** Fixed length (px) of the track's own travel axis — shared by the track, the tick strip, and
 * the tick-label strip so all three always line up exactly. Every other row (readout, end labels)
 * lives *outside* this fixed-length block rather than sharing its space, which is what caused a
 * real bug: end labels used to sit inside the same fixed-height flex box as the vertical track,
 * stealing part of its budget and shifting the track's actual rendered position away from the
 * (unshifted) tick strip beside it. */
/**
 * How long a VERTICAL track is.
 *
 * Horizontal has never needed a constant: it is `w-full` and takes whatever its container gives it,
 * which on a desktop canvas card measures about 514px and on a 390px phone about 218px. Vertical
 * was a flat 192px in every one of those places, so the two orientations were never the same
 * control seen from two angles — the same 0-100 range got 2.7x more travel lying down than standing
 * up, and nothing said so.
 *
 * The phone case was the wrong way round entirely. A 390x844 phone has roughly 690px of usable
 * height against 390px of width; height is the abundant axis and width the scarce one, so vertical
 * is precisely the orientation that should grow there. At a fixed 192px it was SHORTER than the
 * horizontal track on the same screen, using 28% of the axis it exists to exploit. With the default
 * step of 1 that is 1.92px per stop, against a finger contact patch of about 40px and a handle that
 * is itself 20px — a tenth of the whole travel.
 *
 * `compact` is for the two places that are pictures of a slider rather than sliders: the collapsed
 * canvas card's mockup, and the editor's preview box inside a 384px panel, neither of which can
 * absorb 420px. Everything a finger actually touches gets the responsive length.
 *
 * Note the cost, because it is a real one and was previously a deliberate property: the editor's
 * preview is no longer a true scale model of what a student sees. It was, and the comment on
 * `SliderAnswerSummary` still says the card "matches the live/student-facing preview exactly". That
 * held only by making the student's control as small as the editor's picture of it.
 */
const TRACK_LENGTH_COMPACT_PX = 192;
const TRACK_LENGTH_RESPONSIVE = 'clamp(192px, 45vh, 420px)';

/**
 * Standalone continuous single- or dual-handle drag primitive, linear or logarithmic, horizontal
 * or vertical. No precedent for this in the codebase (dnd-kit is list-reordering only) — built
 * from scratch on native Pointer Events, with roving keyboard support mirroring
 * @/components/ui/radio-group.tsx's handleKeyDown, and a self-contained aria-live announcer
 * since this is meant to be a fully self-sufficient primitive component.
 */
export function SliderTrack({
  min, max, scaleMode, stepSize, logStepsPerDecade, value, onTouch, onChange, onCommit,
  valueB, onTouchB, onChangeB, onCommitB, ariaLabelB,
  showLiveReadout, minLabel, maxLabel, orientation, reverseDirection,
  showTickMarks, majorTickInterval, minorTickInterval, showMajorTickValues, tickLabels,
  formatValue, unitSuffix, compact = false,
  ariaLabelNudgeDown, ariaLabelNudgeUp, ariaLabelNudgeDownB, ariaLabelNudgeUpB,
  handleLabel1, handleLabel2,
  disabled, ariaLabel,
}: SliderTrackProps) {
  /**
   * The one string this component builds itself.
   *
   * Every other label arrives as a prop, because the parent knows which handle is which. The marker
   * summary cannot: it has to be formatted by `formatWithUnit` below, so that the value a listener
   * hears for a marker matches the value they hear from the handle. Splitting those across two
   * files is how they would drift.
   */
  const { t } = useTranslation();
  const scaleId = useId();
  const trackRef = useRef<HTMLDivElement>(null);
  const draggingHandleRef = useRef<'a' | 'b' | null>(null);
  const touchedRef = useRef(false);
  const touchedBRef = useRef(false);
  const [announcement, setAnnouncement] = useState('');
  // Which handle renders on top when they visually overlap (dual-handle mode) — purely visual;
  // the actual grab-target resolution when overlapping is handled by pendingAmbiguousRef below.
  const [frontHandle, setFrontHandle] = useState<'a' | 'b'>('b');
  // Set on pointerdown when the two handles are overlapping and it's genuinely ambiguous which
  // one the user means to grab. Resolved on the first real pointermove: dragging "toward
  // increase" commits to handle B (the only one that can actually move up from a coincident
  // position — A's effective max is capped at B's current value), dragging "toward decrease"
  // commits to handle A (the only one that can move down). Cleared once resolved.
  const pendingAmbiguousRef = useRef<{ clientX: number; clientY: number; pointerId: number } | null>(null);

  const isVertical = orientation === 'vertical';
  const isDual = valueB !== undefined;

  function quantize(v: number): number {
    return scaleMode === 'log' ? quantizeLogStep(v, min, logStepsPerDecade) : quantizeToStep(v, min, stepSize);
  }

  // Appends the unit suffix once — used everywhere a SINGLE value is being described (aria text,
  // announcements, single-handle readout). The dual-handle readout instead appends the suffix
  // once at the very end of the whole "A – B" range (see displayValue below), not per side.
  function formatWithUnit(v: number): string {
    return `${formatValue(v)}${unitSuffix ? ` ${unitSuffix}` : ''}`;
  }

  /**
   * The same value, said rather than shown.
   *
   * Only ever differs where the caller's formatter reached for scientific notation: "5e9 years"
   * becomes "5 times 10 to the 9 years". Applied to the formatted string, not to the number, so
   * the spoken value cannot drift from the visible one — see `speakNumber`.
   */
  function speakWithUnit(v: number): string {
    return `${speakNumber(formatValue(v), t)}${unitSuffix ? ` ${unitSuffix}` : ''}`;
  }

  function fractionOf(v: number): number {
    const f = valueToFraction(v, min, max, scaleMode);
    return reverseDirection ? 1 - f : f;
  }

  function valueFromPoint(clientX: number, clientY: number): number {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0 || rect.height === 0) return value;
    let frac = isVertical ? (rect.bottom - clientY) / rect.height : (clientX - rect.left) / rect.width;
    frac = Math.min(1, Math.max(0, frac));
    if (reverseDirection) frac = 1 - frac;
    return fractionToValue(frac, min, max, scaleMode);
  }

  function markTouched(handle: 'a' | 'b') {
    if (handle === 'a') {
      if (touchedRef.current) return;
      touchedRef.current = true;
      onTouch();
    } else {
      if (touchedBRef.current) return;
      touchedBRef.current = true;
      onTouchB?.();
    }
  }

  function clampForHandle(handle: 'a' | 'b', raw: number): number {
    if (!isDual) return Math.min(max, Math.max(min, raw));
    if (handle === 'a') return Math.min(max, Math.max(min, Math.min(raw, valueB!)));
    return Math.min(max, Math.max(min, Math.max(raw, value)));
  }

  function beginDrag(handle: 'a' | 'b', clientX: number, clientY: number) {
    markTouched(handle);
    draggingHandleRef.current = handle;
    const v = clampForHandle(handle, valueFromPoint(clientX, clientY));
    if (handle === 'a') onChange(v); else onChangeB?.(v);
  }

  function handlePointerDown(handle: 'a' | 'b', e: React.PointerEvent) {
    if (disabled) return;
    e.preventDefault();
    // preventDefault() above blocks the browser's default focus-on-pointerdown behavior for
    // the handle button, so it has to be focused explicitly — otherwise clicking the handle
    // silently leaves focus wherever it was before (e.g. the question editor), and keyboard
    // interaction (arrow keys) never reaches the slider.
    (e.target as HTMLElement).focus();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);

    if (isDual && Math.abs(fractionOf(value) - fractionOf(valueB!)) < OVERLAP_FRACTION_THRESHOLD) {
      pendingAmbiguousRef.current = { clientX: e.clientX, clientY: e.clientY, pointerId: e.pointerId };
      return;
    }
    beginDrag(handle, e.clientX, e.clientY);
  }

  function handlePointerMove(e: React.PointerEvent) {
    const pending = pendingAmbiguousRef.current;
    if (pending) {
      const dx = e.clientX - pending.clientX, dy = e.clientY - pending.clientY;
      if (Math.hypot(dx, dy) < AMBIGUOUS_COMMIT_PX) return;
      const movingTowardIncrease = isVertical ? (reverseDirection ? dy > 0 : dy < 0) : (reverseDirection ? dx < 0 : dx > 0);
      const handle: 'a' | 'b' = movingTowardIncrease ? 'b' : 'a';
      pendingAmbiguousRef.current = null;
      beginDrag(handle, e.clientX, e.clientY);
      return;
    }
    const handle = draggingHandleRef.current;
    if (!handle) return;
    const v = clampForHandle(handle, valueFromPoint(e.clientX, e.clientY));
    if (handle === 'a') onChange(v); else onChangeB?.(v);
  }

  function handlePointerUp(e: React.PointerEvent) {
    pendingAmbiguousRef.current = null;
    const handle = draggingHandleRef.current;
    if (!handle) return;
    draggingHandleRef.current = null;
    const raw = clampForHandle(handle, valueFromPoint(e.clientX, e.clientY));
    const quantized = clampForHandle(handle, quantize(raw));
    if (handle === 'a') onCommit(quantized); else onCommitB?.(quantized);
    setAnnouncement(speakWithUnit(quantized));
    setFrontHandle(handle === 'a' ? 'b' : 'a');
  }

  /** Returns what was actually committed — clamped and quantized — which the nudge repeat needs so
   *  it can advance from reality rather than from what it asked for. */
  function commitStep(handle: 'a' | 'b', next: number): number {
    const quantized = clampForHandle(handle, quantize(next));
    if (handle === 'a') { onChange(quantized); onCommit(quantized); } else { onChangeB?.(quantized); onCommitB?.(quantized); }
    setAnnouncement(speakWithUnit(quantized));
    setFrontHandle(handle === 'a' ? 'b' : 'a');
    return quantized;
  }

  function stepValue(from: number, steps: number): number {
    if (scaleMode === 'log') return logStepValue(from, min, logStepsPerDecade, steps);
    return from + steps * stepSize;
  }

  function handleKeyDown(handle: 'a' | 'b', current: number, e: React.KeyboardEvent) {
    if (disabled) return;
    // ArrowRight/Up must always move the handle toward the *visual* end of the track, matching
    // pointer-drag (valueFromPoint flips the same way — see the reverseDirection comment there).
    // Home/End are deliberately NOT flipped: per the standard slider keyboard pattern they always
    // mean "jump to the minimum/maximum value," independent of which end of the track that value
    // happens to render at.
    const dir = reverseDirection ? -1 : 1;
    let next: number | null = null;
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp') next = stepValue(current, dir);
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') next = stepValue(current, -dir);
    else if (e.key === 'PageUp') next = stepValue(current, 10 * dir);
    else if (e.key === 'PageDown') next = stepValue(current, -10 * dir);
    else if (e.key === 'Home') next = min;
    else if (e.key === 'End') next = max;
    if (next === null) return;
    e.preventDefault();
    markTouched(handle);
    commitStep(handle, next);
  }

  /**
   * The nudge pair: one lattice step per press, for the values a finger cannot land on.
   *
   * A log slider from 1 to 1000 at 900 steps per decade has 2,700 stops on a 514px track — about
   * five stops per pixel, so roughly four in five cannot be reached by dragging at all, and which
   * four is an artifact of pixel rounding rather than anything the teacher chose. Keyboard users
   * already had the answer (ArrowRight/Left call `stepValue`); this is the same behaviour handed to
   * a pointer, which also means touch stops being the only input that cannot reach a stop.
   *
   * It calls `stepValue` and `commitStep` — the keyboard's own functions, not a second copy — so
   * log stepping, quantisation, clamping, the announcement and the dual-handle front/back swap all
   * come along for free and cannot drift.
   *
   * `−`/`+` change the VALUE, and sit next to the readout rather than at the ends of the track.
   * Two reasons. End-mounted arrows would have to flip with `reverseDirection` (a left arrow means
   * "less" only when the scale runs left to right), which is a correctness trap with no upside;
   * value-signed buttons are orientation- and direction-independent. And a phone track is about
   * 218px, so two buttons flanking it would spend the scarcest axis on the device — beside the
   * readout they cost the track nothing.
   *
   * Only rendered when a lattice exists. A continuous slider has no "one step" to nudge by.
   */
  /**
   * Hold-to-repeat, with a delay before it starts.
   *
   * Without the delay a single deliberate tap fires twice on a slow finger; without the repeat,
   * covering any distance is dozens of taps. Both intervals are the platform-conventional ones for
   * a stepper. Cleared on unmount as well as on release, because a pointer that leaves the window
   * mid-press never delivers pointerup.
   */
  const repeatRef = React.useRef<{ delay?: number; tick?: number }>({});
  const stopRepeat = React.useCallback(() => {
    window.clearTimeout(repeatRef.current.delay);
    window.clearInterval(repeatRef.current.tick);
    repeatRef.current = {};
  }, []);
  React.useEffect(() => stopRepeat, [stopRepeat]);
  const startRepeat = (fn: () => void) => {
    stopRepeat();
    repeatRef.current.delay = window.setTimeout(() => {
      repeatRef.current.tick = window.setInterval(fn, 60);
    }, 400);
  };

  const hasLattice = scaleMode === 'log'
    ? Number.isFinite(logStepsPerDecade) && logStepsPerDecade > 0 && min > 0
    : Number.isFinite(stepSize) && stepSize > 0;
  /*
   * The LIVE value, read through a ref.
   *
   * `setInterval` calls one closure forever, and a closure over the `value` prop is frozen at the
   * render that created it — so hold-to-repeat fired on schedule and every tick recomputed the same
   * next value from the same stale input. The handle moved once and stopped, which reads exactly
   * like the repeat not working at all. Keyboard auto-repeat was unaffected because each key event
   * carries its own current value.
   */
  const valueRef = useRef(value);
  const lastNudgedRef = useRef<number | null>(null);
  const lastNudgedBRef = useRef<number | undefined>(undefined);
  /*
   * Resync from the prop only when the change came from SOMEWHERE ELSE — a drag, a reset, the
   * parent. Assigning unconditionally on every render looks equivalent and is not: a repeat tick
   * that produces a value the parent already holds makes `setState` a no-op, React skips the
   * re-render, and a ref that only advances on render then stays frozen. The observed shape was
   * 51, 52, 52, 52 — one good step, then stuck forever, which is worse than not repeating at all
   * because it looks like the button works.
   */
  if (value !== lastNudgedRef.current) valueRef.current = value;
  const valueBRef = useRef(valueB);
  if (valueB !== lastNudgedBRef.current) valueBRef.current = valueB;
  const nudge = (handle: 'a' | 'b', steps: number) => {
    if (disabled) return;
    markTouched(handle);
    const live = handle === 'b' ? valueBRef.current : valueRef.current;
    const from = quantize(Math.min(max, Math.max(min, live ?? min)));
    // Advance the ref from what was actually committed (clamped and quantized), not from what we
    // asked for, so a hold that reaches the end of the track stops there instead of running off.
    const committed = commitStep(handle, stepValue(from, steps));
    if (handle === 'b') { valueBRef.current = committed; lastNudgedBRef.current = committed; }
    else { valueRef.current = committed; lastNudgedRef.current = committed; }
  };
  /*
   * Placed by ORIENTATION, and away from the readout.
   *
   * They sat beside the live number first, which put them on a row whose width changes with every
   * value — "9" to "283.75" moves the buttons sideways under a finger that is holding one down.
   * A stepper has to stay still. Horizontal puts them under the track, vertical to its right: both
   * are fixed positions, and both are the axis with room to spare in that orientation.
   *
   * Still `−`/`+` rather than directional arrows, and still value-signed: `−` is a smaller number
   * in both orientations and under `reverseDirection`, which end-mounted arrows could not manage
   * without flipping.
   */
  /*
   * Rendered even when DISABLED, greyed and inert.
   *
   * The canvas card is a picture of the block, and a picture that omits a control is a picture of a
   * different block — a teacher scanning the canvas should see the same slider a student will,
   * minus the ability to move it. Hiding the pair there (the first version did) meant the card and
   * the preview disagreed about what the block even has.
   */
  /**
   * One pair per slider.
   *
   * A single `−`/`+` was ambiguous with two handles, so dual-handle got nothing at all — which left
   * the orientation that most needs fine-tuning (a dense lattice plus two answers to hit) as the one
   * without it. Two labelled pairs are unambiguous: the label says which slider moves, so nothing
   * depends on remembering which handle was touched last.
   */
  /*
   * Which way the control points, derived once from the axis and the scale's direction.
   *
   * `+` belongs on the side the handle travels toward as the value rises, so the button and the
   * motion agree. That is the top in a normal vertical scale and the right in a normal horizontal
   * one — and `reverseDirection` flips BOTH, which is the case the first version got wrong: it keyed
   * only on orientation, so inverting the scale left `−` sitting where the handle moves up.
   *
   *   horizontal, normal    values rise rightward   -> minus, plus
   *   horizontal, reversed  values rise leftward    -> plus, minus
   *   vertical,   normal    values rise upward      -> plus, minus
   *   vertical,   reversed  values rise downward    -> minus, plus
   *
   * And for TWO sliders, each pair sits at the end its own handle occupies: slider 1 holds the lower
   * value, so it belongs at the low end of the track, wherever that end currently is. Reading order
   * therefore starts with slider 1 except where the low end is last.
   */
  const plusFirst = isVertical ? !reverseDirection : reverseDirection;
  const sliderOneFirst = isVertical ? reverseDirection : !reverseDirection;

  const nudgeGroup = (handle: 'a' | 'b') => (
    <span className={`inline-flex items-center gap-1.5 ${isVertical ? 'flex-col' : ''}`}>
      {/*
        * `+` goes on the side the HANDLE MOVES TOWARD when the value rises. One rule, all four
        * combinations — see `plusFirst` above, which is where the axis and `reverseDirection` are
        * reconciled once instead of at each of these sites.
        *
        * The array is reordered rather than using `flex-row-reverse`/`flex-col-reverse`, so DOM
        * order matches visual order: tabbing through a pair that reads backwards is its own bug.
        */}
      {(plusFirst
        ? ([[1, '+', 'increase'], [-1, '−', 'decrease']] as const)
        : ([[-1, '−', 'decrease'], [1, '+', 'increase']] as const)
      ).map(([steps, glyph, dir]) => (
        <button
          key={dir}
          type='button'
          // Hold to repeat: one stop at a time is right for precision and wrong for distance, and
          // 283 to 288 is five presses. `onPointerDown` rather than `onClick` so the repeat can
          // start immediately and stop on release anywhere.
          disabled={disabled}
          /*
           * `pointer-events-none` when disabled, so a click lands on whatever is BEHIND.
           *
           * The canvas card opens the block when clicked, and a disabled button is still a hit-test
           * target: it swallows the press without firing anything, so the stepper became a dead
           * patch on a card that is otherwise entirely clickable. Letting the press through means
           * the greyed buttons behave like the rest of the picture — click anywhere, edit the block.
           */
          onPointerDown={disabled ? undefined
            : e => { e.preventDefault(); nudge(handle, steps); startRepeat(() => nudge(handle, steps)); }}
          onPointerUp={stopRepeat}
          onPointerLeave={stopRepeat}
          onPointerCancel={stopRepeat}
          aria-label={isDual && handle === 'b'
            ? (dir === 'decrease' ? ariaLabelNudgeDownB : ariaLabelNudgeUpB)
            : (dir === 'decrease' ? ariaLabelNudgeDown : ariaLabelNudgeUp)}
          /*
           * 32px of visible chrome, 44px of TAP TARGET.
           *
           * These were 24px square, which clears the WCAG 2.5.8 floor and nothing else: Apple asks
           * for 44pt and Material for 48dp, and this is a control a student presses repeatedly on a
           * phone, often several times in a row. Drawing 44px of button beside a slider would
           * dominate it, so the visible box stays small and `after:-inset-1.5` extends the hit area
           * past it — the pseudo-element is inside the button, so it is the button that gets hit.
           */
          className='relative flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-ws-border bg-ws-btn text-base font-medium leading-none text-ws-muted transition-colors after:absolute after:-inset-1.5 after:content-[""] disabled:pointer-events-none disabled:opacity-40 enabled:hover:border-ws-btn-active-fg enabled:hover:text-ws-btn-active-fg focus-visible:ring-2 focus-visible:ring-ws-primary focus-visible:ring-offset-1'
        >
          {glyph}
        </button>
      ))}
    </span>
  );
  const nudgeButtons = !hasLattice ? null : !isDual ? nudgeGroup('a') : (
    /*
     * Two labelled groups, arranged by orientation.
     *
     * VERTICAL reads pair-then-label along a ROW, because the pair is already a vertical stack and
     * putting the label above it made a four-deep column per slider — tall, and hard to tell which
     * label belonged to which pair. There is spare width beside a vertical track, so the label goes
     * there and centres against the two buttons. The groups need real space between them or the two
     * pairs read as one column of four buttons.
     *
     * HORIZONTAL keeps label-then-pair, reading left to right as a caption for what follows.
     */
    <span className={`inline-flex ${isVertical ? 'flex-col gap-5' : 'items-center gap-5'}`}>
      {(sliderOneFirst ? (['a', 'b'] as const) : (['b', 'a'] as const)).map(h => (
        <span key={h} className='inline-flex items-center gap-2'>
          {/*
            * `text-xs`, not `text-[10px]`.
            *
            * 10px was below this app's own smallest conventional size, and these labels are not
            * decoration — they are the only thing saying which pair moves which slider, so someone
            * who cannot read them cannot use the control at all. 12px is the size every other
            * secondary label here uses, and `font-medium` keeps it legible against the buttons.
            */}
          {isVertical && nudgeGroup(h)}
          <span aria-hidden='true' className='select-none whitespace-nowrap text-xs font-medium text-ws-muted'>
            {h === 'b' ? handleLabel2 : handleLabel1}
          </span>
          {!isVertical && nudgeGroup(h)}
        </span>
      ))}
    </span>
  );

  // The live readout always shows the quantized value, never the raw drag position — the handle
  // itself still tracks the pointer continuously (smooth visual feel), but displaying in-between
  // values that aren't actually reachable/selectable is misleading (e.g. a 0-10 range with step 1
  // should only ever show "4", never "4.37"). Dual-handle readout appends the unit suffix once at
  // the end ("5 – 15 days"), not once per side ("5 days – 15 days").
  const displayValue = showLiveReadout
    ? (isDual
        ? `${formatValue(quantize(Math.min(max, Math.max(min, value))))} – ${formatValue(quantize(Math.min(max, Math.max(min, valueB!))))}${unitSuffix ? ` ${unitSuffix}` : ''}`
        : formatWithUnit(quantize(Math.min(max, Math.max(min, value)))))
    : null;

  // One value, used by the track and by the tick/label strips beside it — they share a length so
  // a tick at 40 lines up with the 40 on the rail. See the constants' own note.
  const trackLength = compact ? `${TRACK_LENGTH_COMPACT_PX}px` : TRACK_LENGTH_RESPONSIVE;
  const ticks = showTickMarks ? computeTickPositions(min, max, scaleMode, majorTickInterval ?? 0, minorTickInterval ?? 0) : { major: [], minor: [] };
  const hasCustomTickLabels = !!(tickLabels && tickLabels.length > 0);
  const hasTickLabels = hasCustomTickLabels || !!showMajorTickValues;

  function renderHandle(handle: 'a' | 'b', v: number, label: string) {
    const frac = fractionOf(v);
    // Both position anchors are computed together per orientation — never mix a class-based
    // `top-1/2` with an inline `bottom` (or vice versa): a box with top, bottom, AND an implicit
    // height all set is over-constrained, and the browser silently drops `bottom`/`right`,
    // pinning the handle in place regardless of the intended fraction. This was a real bug in
    // vertical mode (handles updated their aria-valuenow correctly but never visually moved).
    const posStyle = isVertical
      ? { left: '50%', bottom: `${frac * 100}%` }
      : { top: '50%', left: `${frac * 100}%` };
    // `translateY` direction must flip for `bottom`-positioned elements: `top: Y` + `translateY(-50%)`
    // centers correctly (both "grow downward" in the same coordinate sense), but `bottom: Y` places
    // the element's *bottom* edge Y away from the container's bottom — centering it needs
    // `translateY(+50%)` instead, since translateY always moves in the standard top-down screen
    // direction regardless of which property (top/bottom) positioned the element. Using the same
    // `-translate-y-1/2` for both was a real bug: it rendered the vertical handle a full 20px
    // (its own height) too high, even though the underlying value/hit-area math was correct.
    const isFront = !isDual || frontHandle === handle;
    return (
      <button
        type='button'
        role='slider'
        aria-label={label}
        aria-describedby={scaleId}
        aria-orientation={orientation}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={Math.min(max, Math.max(min, v))}
        aria-valuetext={speakWithUnit(Math.min(max, Math.max(min, v)))}
        aria-disabled={disabled || undefined}
        tabIndex={disabled ? -1 : 0}
        onKeyDown={e => handleKeyDown(handle, v, e)}
        onPointerDown={e => handlePointerDown(handle, e)}
        disabled={disabled}
        className={`absolute flex h-5 w-5 -translate-x-1/2 ${isVertical ? 'translate-y-1/2' : '-translate-y-1/2'} items-center justify-center rounded-full border-2 border-ws-primary bg-ws-btn shadow-sm transition-colors focus-visible:[outline:2px_auto] disabled:cursor-not-allowed ${isFront ? 'z-10' : 'z-0'}`}
        style={posStyle}
      />
    );
  }

  const track = (
    <div
      ref={trackRef}
      // `isolate` scopes the handles' z-10 to this track. Without it those values compete with the
      // whole page: the editor's sticky rich-text toolbar is also z-10, and the slider preview sits
      // LATER in the DOM, so the tie broke in the handle's favour and it painted straight over the
      // toolbar while scrolling. Exactly the bug rich-text-editor.tsx documents for its placeholder,
      // same cause. Fixed here rather than at one call site so every mount of the track inherits it.
      className={`relative isolate rounded-full bg-ws-border ${disabled ? 'opacity-40' : ''} ${isVertical ? 'h-full w-2' : 'h-2 w-full'}`}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
    >
      {!isDual && (
        <div
          className={`absolute rounded-full bg-ws-primary/40 ${isVertical ? 'inset-x-0 bottom-0' : 'inset-y-0 left-0'}`}
          style={isVertical ? { height: `${fractionOf(value) * 100}%` } : { width: `${fractionOf(value) * 100}%` }}
        />
      )}
      {isDual && (() => {
        const fA = fractionOf(value), fB = fractionOf(valueB!);
        const lo = Math.min(fA, fB), hi = Math.max(fA, fB);
        return (
          <div
            className={`absolute rounded-full bg-ws-primary/40 ${isVertical ? 'inset-x-0' : 'inset-y-0'}`}
            style={isVertical ? { bottom: `${lo * 100}%`, height: `${(hi - lo) * 100}%` } : { left: `${lo * 100}%`, width: `${(hi - lo) * 100}%` }}
          />
        );
      })()}
      {renderHandle('a', value, ariaLabel)}
      {isDual && renderHandle('b', valueB!, ariaLabelB ?? ariaLabel)}
    </div>
  );

  // Tick marks render as their own strip *adjacent to* the track (above it for horizontal, beside
  // it for vertical) rather than drawn on top of the track itself — majors render taller/bolder,
  // minors shorter, independent of the step size (tick frequency and step granularity are
  // separate concerns: a coarse step can still want fine visual ticks, or vice versa).
  const tickStrip = showTickMarks ? (
    <div className={`relative shrink-0 ${isVertical ? 'h-full w-3' : 'h-3 w-full'}`}>
      {ticks.minor.map(t => {
        const f = fractionOf(t);
        return (
          <span
            key={`mn-${t}`}
            aria-hidden='true'
            className={`absolute bg-ws-muted/40 ${isVertical ? 'right-0 h-px w-1.5' : 'bottom-0 h-1.5 w-px'}`}
            style={isVertical ? { bottom: `${f * 100}%` } : { left: `${f * 100}%` }}
          />
        );
      })}
      {ticks.major.map(t => {
        const f = fractionOf(t);
        return (
          <span
            key={`mj-${t}`}
            aria-hidden='true'
            className={`absolute bg-ws-muted/70 ${isVertical ? 'right-0 h-0.5 w-3' : 'bottom-0 h-3 w-0.5'}`}
            style={isVertical ? { bottom: `${f * 100}%` } : { left: `${f * 100}%` }}
          />
        );
      })}
    </div>
  ) : null;

  // Custom tick *labels* (arbitrary text at specific values) and auto-rendered major-tick values
  // share one strip, sized to exactly match the track's travel length so their positions line up
  // — rendered above the tick marks for horizontal (reading order: tick labels, tick marks,
  // track), beside them for vertical. Kept as a normal-flow element (not absolutely positioned on
  // top of the track) so it always has real layout height and can never get visually clipped by
  // its container. Same translateY-direction fix as the handle: `bottom`-positioned elements in
  // vertical mode need `translateY(+50%)` to center, not `-50%`.
  //
  // When BOTH are showing, they get separate lanes — custom labels sit further from the tick
  // marks (an "outer" lane) than the auto numeric values (an "inner" lane, right against the
  // marks) — otherwise a custom label and a numeric value anchored at the same value would print
  // on top of each other.
  /**
   * Two lanes, and a MARKER IS ALWAYS IN THE OUTER ONE.
   *
   * The inner lane is flush with the strip's track-facing edge; the outer sits a step back from it.
   * Markers used to take the inner lane whenever major-tick values were switched off, on the
   * reasoning that an empty lane is wasted space. What that actually produced was a marker printed
   * hard against the rail with its diamond half on top of it — the label had no room to breathe and
   * read as part of the track rather than as an annotation of it.
   *
   * Keeping markers in one lane regardless also makes the four states behave the same way. Turning
   * tick values on used to MOVE every marker sideways, so a layout tuned with them off came apart
   * when they went on.
   *
   * The lane is nearer the track when it is the only one occupied — there is no number column to
   * clear, so the gap only needs to separate the label from the rail, not from another row of text.
   */
  const bothTickLabelLanes = !!showMajorTickValues && hasCustomTickLabels;
  /*
   * VERTICAL puts the two lanes side by side at the same height, so the gutter between them has to
   * be a real column boundary rather than a gap that happens to look right.
   *
   * Tick values are right-aligned at the strip's inner edge and grow LEFTWARD as they gain digits,
   * so a fixed offset for the marker lane produced a gutter that shrank with the number's width:
   * measured at 7px against "40", and about 1px against "100". Two right-aligned pieces of text one
   * pixel apart read as one phrase — which is exactly what "mama mias 40" looked like.
   *
   * A fixed-width box for the numbers fixes the column edge in place, so the gutter is the same
   * whatever the scale runs to. w-8 fits "100" at this font with room to spare; the values stay
   * right-aligned inside it, so the column reads flush against the track as before.
   *
   * HORIZONTAL has no such problem — the lanes are stacked rows, already 20px apart — so it keeps
   * its simple offsets.
   */
  const innerLaneClass = isVertical
    ? 'right-0 w-8 translate-y-1/2 text-right' : 'bottom-0 -translate-x-1/2';

  /*
   * ONE number for where the marker lane sits, in pixels.
   *
   * It used to be two: a Tailwind offset class for the label, and a separate `STEM_LENGTH_PX`
   * constant for the line reaching from it to the diamond. Moving the lane without moving the
   * constant left the stem ending 24px short of its own label — a line floating in the middle of
   * nowhere. The class is generated from the number now, so they cannot drift again.
   */
  /*
   * Orientation-specific, because the two lanes are not the same kind of thing.
   *
   * VERTICAL stacks them side by side, so the lane has to clear the whole tick-number COLUMN — 32px
   * of fixed-width box plus a gutter. HORIZONTAL stacks them as rows, so it only has to clear one
   * line of text. Using the vertical figure for both (which a first pass did) put the horizontal
   * marker 8px above its own strip and straight through the live readout above it.
   */
  const MARKER_LANE_PX = bothTickLabelLanes ? (isVertical ? 44 : 20) : 10;
  /** Horizontal markers are CENTRED on their position, so a long one overflows both ways and has
   *  no strip edge to be bounded by. 112px is about eighteen characters at this size, and two
   *  markers that close together were already going to collide whatever the cap. */
  const MARKER_MAX_WIDTH_PX = 112;
  const markerLaneClass = isVertical ? 'translate-y-1/2 text-right' : '-translate-x-1/2';
  const markerLaneStyle: React.CSSProperties = isVertical
    ? { right: MARKER_LANE_PX } : { bottom: MARKER_LANE_PX };
  /** Enough room for whichever lanes are actually occupied — see markerLaneClass. */
  const stripSize = isVertical
    ? (bothTickLabelLanes ? 'w-28' : hasCustomTickLabels ? 'w-24' : 'w-16')
    : (bothTickLabelLanes ? 'h-9' : hasCustomTickLabels ? 'h-7' : 'h-4');
  const tickLabelStrip = hasTickLabels ? (
    <div className={`relative shrink-0 ${isVertical ? `h-full ${stripSize}` : `w-full ${stripSize}`}`}>
      {showMajorTickValues && ticks.major.map(t => {
        const f = fractionOf(t);
        return (
          <span
            key={`mjv-${t}`}
            aria-hidden='true'
            className={`absolute whitespace-nowrap text-[10px] text-ws-muted ${innerLaneClass}`}
            style={isVertical ? { bottom: `${f * 100}%` } : { left: `${f * 100}%` }}
          >
            {formatValue(t)}
          </span>
        );
      })}
      {hasCustomTickLabels && tickLabels!.map(tl => {
        // A label whose value falls outside [min, max] has no real position on the track —
        // valueToFraction would clamp it to 0 or 1, which visually "smashes" it against the end
        // of the scale as if it belonged there. Hidden entirely instead (the editor surfaces a
        // warning so the teacher notices and fixes the value rather than wondering where it went).
        if (tl.value < min || tl.value > max) return null;
        const f = fractionOf(tl.value);
        // A custom label's value doesn't necessarily land on an actual tick mark (e.g. a label
        // at 17 on a 0-100 scale with marks every 10) — a small diamond pinpoints the exact
        // position the label refers to, with a thin stem connecting it back up to the label
        // text. The diamond sits just past the strip's own inner edge (into the gap before the
        // tick-marks strip) rather than flush with it — flush placement put it directly on top
        // of the auto-rendered major-tick numbers whenever both are showing, since those numbers
        // are anchored to that exact same edge. `POINTER_OFFSET_PX` is the diamond's distance
        // past the edge; `STEM_LENGTH_PX` is how far the stem reaches back up to the label's own
        // anchor line (short when the label shares the inner lane, longer when it's pushed to the
        // outer lane by major-tick numbers occupying the inner one).
        /*
         * The stem runs from the label's lane to the diamond's NEAR VERTEX, and stops there.
         *
         * It used to run to the diamond's far side and pass straight underneath it. The diamond is
         * only 70% opaque, so the line showed through the middle of it — a leader arriving at a
         * marker should end at the marker, not be visible crossing it.
         *
         * The vertex is where it ends because the diamond is a square turned 45°: its widest point
         * on this axis is a corner, half a diagonal from the centre, not half a side. Ending at
         * half a SIDE would leave the last 1.2px of line poking out past the corner on both
         * diagonals, which at this size is exactly the sliver that reads as a glitch.
         */
        const POINTER_OFFSET_PX = 6;                 // the diamond box's outer edge, past the strip
        const DIAMOND_PX = 6;                        // h-1.5 w-1.5
        // Positive = inward, toward the label. The box's outer edge sits POINTER_OFFSET_PX outside
        // the strip, so its centre is half a box further in, and the vertex half a diagonal beyond.
        const diamondCentreIn = -(POINTER_OFFSET_PX - DIAMOND_PX / 2);
        const stemEndIn = diamondCentreIn + (DIAMOND_PX * Math.SQRT2) / 2;
        /*
         * A little air between the text and the line, in VERTICAL only.
         *
         * There the two are end to end on the same row, so a stem starting flush against the last
         * letter reads as an underscore continuing the word. Horizontal stacks them — the label sits
         * on the row above and the line drops away from it — where a break would just look detached.
         */
        const LABEL_STEM_GAP_PX = isVertical ? 4 : 0;
        const STEM_LENGTH_PX = MARKER_LANE_PX - stemEndIn - LABEL_STEM_GAP_PX;
        return (
          <React.Fragment key={tl.id}>
            {/*
              * Capped, and ellipsised past the cap.
              *
              * The label is absolutely positioned and was `whitespace-nowrap` with no maximum, so a
              * long one simply kept going — leftward out of the strip in vertical, and out of both
              * sides of the card in horizontal, since it is centred there. The editor caps what can
              * be typed; this caps what can be DRAWN, which is the half that has to hold when a
              * label arrives from an older task that predates the input limit.
              *
              * `title` carries the full text for a mouse, and the editor's own field always shows
              * it in full — nothing is only visible here.
              */}
            <span
              aria-hidden='true'
              title={tl.label}
              className={`absolute overflow-hidden text-ellipsis whitespace-nowrap text-[10px] text-ws-muted ${markerLaneClass}`}
              style={{
                ...markerLaneStyle,
                ...(isVertical ? { bottom: `${f * 100}%` } : { left: `${f * 100}%` }),
                maxWidth: isVertical ? `calc(100% - ${MARKER_LANE_PX}px)` : MARKER_MAX_WIDTH_PX,
              }}
            >
              {tl.label}
            </span>
            {/* z-0/z-10 make the stacking explicit (stem behind, diamond in front) rather than
                relying on DOM order alone — the diamond sits right at the stem's end, so without
                an explicit order a later refactor could easily flip which one paints on top. */}
            <span
              aria-hidden='true'
              className={`absolute z-0 bg-ws-muted/50 ${isVertical ? 'h-px translate-y-1/2' : 'w-px -translate-x-1/2'}`}
              style={isVertical
                ? { bottom: `${f * 100}%`, right: stemEndIn, width: STEM_LENGTH_PX }
                : { left: `${f * 100}%`, bottom: stemEndIn, height: STEM_LENGTH_PX }}
            />
            <span
              aria-hidden='true'
              className={`absolute z-10 h-1.5 w-1.5 rotate-45 bg-ws-muted/70 ${isVertical ? 'translate-y-1/2' : '-translate-x-1/2'}`}
              style={isVertical
                ? { bottom: `${f * 100}%`, right: -POINTER_OFFSET_PX }
                : { left: `${f * 100}%`, bottom: -POINTER_OFFSET_PX }}
            />
          </React.Fragment>
        );
      })}
    </div>
  ) : null;

  // Horizontal end labels render *below* the track (reading order: readout, tick labels, tick
  // marks, track, min/max labels) — capped to less than half the row's width (with wrapping) so
  // two long labels can never collide with each other.
  const showHorizontalLabels = !isVertical && (minLabel || maxLabel);
  const startLabelText = reverseDirection ? maxLabel : minLabel;
  const endLabelText = reverseDirection ? minLabel : maxLabel;

  if (isVertical) {
    const topLabelText = reverseDirection ? minLabel : maxLabel;
    const bottomLabelText = reverseDirection ? maxLabel : minLabel;
    const hasTickAssembly = !!(tickStrip || tickLabelStrip);
    return (
      <div className='flex w-full flex-col items-center gap-2'>
        {showLiveReadout && <div className='text-sm font-medium text-ws-text'>{displayValue}</div>}
        {/* mb-2 on top of the column's gap-2: in vertical these labels butt against the ends of
            the track, where the handle also parks at its extremes. */}
        {topLabelText && <span className='mb-2 max-w-24 shrink-0 break-words text-center text-xs text-ws-muted'>{topLabelText}</span>}
        {/* The track is centered on its own — the tick labels/marks attach to its left via
            absolute positioning instead of sharing the centering row, so they never pull the
            track itself off-center. Centering the whole [ticks+track] assemblage as one block
            visually worked out to the track (what the reader's eye follows, and what the readout
            above is centered over) sitting off to one side whenever ticks were showing. All three
            of the track/tick-marks/tick-labels pieces still share the exact same fixed length
            (`trackLength`) so they line up — that part is unchanged from the earlier alignment
            fix; only how the assembly is centered as a whole has changed. */}
        {/* The stepper hangs off the track's RIGHT edge, mirroring how the tick strips hang off its
            left — so the track itself stays centred and neither side pulls it off-axis. */}
        <div className='relative flex justify-center' style={{ height: trackLength }}>
          {/*
            * ml-7, not ml-2. The clearance has to be measured from the HANDLE, not the rail: the rail
            * is 8px wide and the handle is 20px centred on it, so the handle already sticks out 6px
            * past the rail's edge. At ml-2 the buttons sat 2px from a handle a finger is about to
            * grab — close enough to hit the wrong one. 28px leaves 22px of real gap.
            */}
          {nudgeButtons && (
            <div className='absolute left-full ml-7 flex h-full items-center'>{nudgeButtons}</div>
          )}
          {hasTickAssembly && (
            <div className='absolute right-full mr-2 flex items-stretch gap-2' style={{ height: trackLength }}>
              {tickLabelStrip}
              {tickStrip}
            </div>
          )}
          {track}
        </div>
        {bottomLabelText && <span className='mt-2 max-w-24 shrink-0 break-words text-center text-xs text-ws-muted'>{bottomLabelText}</span>}
        <div aria-live='polite' aria-atomic='true' className='sr-only'>{announcement}</div>
      </div>
    );
  }

  return (
    <div className='flex w-full flex-col gap-2'>
      {showLiveReadout && <div className='text-center text-sm font-medium text-ws-text'>{displayValue}</div>}
      {/* Track/tick-marks/tick-labels/end-labels share a narrower-than-full-width lane (not the
          readout above, which just wraps normally if long) — this problem is layout-driven, not
          viewport-driven: a tick label or major-tick value anchored exactly at fraction 0 or 1 is
          centered on that exact point (translateX(-50%)), so half its text extends past the
          track's own edge with nothing to stop it. At full container width that overhang runs
          straight into the block display card's outer boundary and whatever sits beside it,
          regardless of screen size — reproduces with "10,000"/"0.00001" on desktop just as
          readily as on phone. Reserving a fixed side margin gives that overhang somewhere to go. */}
      <div className='mx-auto flex w-4/5 flex-col gap-2'>
        {tickLabelStrip}
        {tickStrip}
        <div style={{ height: 8 }}>{track}</div>
        {/* `aria-hidden`, for the same reason the marker spans below are: read in DOM order these are
            two bare phrases — "Earth's Birth Today" — and the sr-only scale sentence a few lines
            down already says each label WITH the value it marks, which is the half that means
            anything. They were the one part of the rail still exposed. */}
        {showHorizontalLabels && (
          <div aria-hidden='true' className='flex justify-between gap-2'>
            <span className='max-w-[45%] break-words text-left text-xs text-ws-muted'>{startLabelText}</span>
            <span className='max-w-[45%] break-words text-right text-xs text-ws-muted'>{endLabelText}</span>
          </div>
        )}
      </div>
      {/*
        What the scale IS, before anything about where the handles are.

        A sighted student takes the range off the rail at a glance. A listener was told only the
        current value: "Slider handle, slider, 0 years" says nothing about whether the next step is
        a year or a billion of them. Readers do not reliably announce `aria-valuemin`/`max`, so the
        range has to be stated.

        Both delivery paths, deliberately. As sr-only text it lands in reading order right after the
        question, which is where a teacher reviewing the task meets it and what Preview's
        read-through shows. As `aria-describedby` on each handle it also reaches a student who tabs
        straight to the control and never browses — a description is announced on focus and NOT
        re-announced on every arrow keypress, so it costs nothing per step.

        The end LABELS come with it. "from 0 years to 5 times 10 to the 9 years" is the arithmetic;
        "0 years, today" and "5 times 10 to the 9 years, earth's birth" is what the teacher wrote on
        the rail, and it is the half that says what the numbers mean.
      */}
      <p id={scaleId} data-authored='' className='sr-only'>
        {t('blocks.slider.scaleA11y', {
          min: minLabel ? t('blocks.slider.endLabelledA11y', { value: speakWithUnit(min), label: minLabel }) : speakWithUnit(min),
          max: maxLabel ? t('blocks.slider.endLabelledA11y', { value: speakWithUnit(max), label: maxLabel }) : speakWithUnit(max),
        })}
      </p>
      {/* The markers, for anyone who cannot see them.

          Each visible marker span is `aria-hidden` and has to stay that way: they are absolutely
          positioned, truncated with an ellipsis, and interleaved with tick values, so read in DOM
          order they arrive as scattered fragments with no indication of what value each sits at.
          But hiding them was the whole story until now, which meant a label a teacher wrote AT a
          value — "GOE", "Cambrian Explosion" — reached no listener at all. Authored content that
          never arrived.

          So one sr-only sentence carries the set, each label with the value it marks, formatted by
          the same `formatWithUnit` the handle's `aria-valuetext` uses so the two agree. Marked
          `data-authored` because it IS the teacher's content, which also puts it in Preview's
          read-through. */}
      {hasCustomTickLabels && (
        <p data-authored='' className='sr-only'>
          {t('blocks.slider.markersA11y', {
            markers: tickLabels!
              .filter(tl => tl.value >= min && tl.value <= max)
              .map(tl => t('blocks.slider.markerAtValueA11y', { label: tl.label, value: speakWithUnit(tl.value) }))
              // A FULL STOP between markers, a comma inside one. Punctuation is the only pause a
              // page can ask for — there is no SSML in content — and joining with commas throughout
              // ran the set together as one breathless list where the break between "GOE, at 2.5
              // times 10 to the 9 years" and the next label was no longer than the one before its
              // own value.
              .join('. '),
          })}
        </p>
      )}
      {/* Below everything, centred: a fixed spot that does not move as the readout's width changes
          with the value under a finger that is holding a button down. */}
      {nudgeButtons && <div className='flex justify-center'>{nudgeButtons}</div>}
      <div aria-live='polite' aria-atomic='true' className='sr-only'>{announcement}</div>
    </div>
  );
}
